import { Table } from '@components/shared/Table'
import { ColumnDef } from '@tanstack/react-table'
import CommonPool from 'abis/CommonPool.json'
import {
  useMarketTokensData,
  useMarkets,
  useMarketsInfo,
} from 'domain/synthetics/markets'
import { ethers } from 'ethers'
import { getProvider } from 'rfx/lib/rpc'
import useWallet from 'rfx/lib/wallets/useWallet'
import useBreakpoints from 'hooks/useBreakpoints'
import { useMemo } from 'react'
import useSWR from 'swr'
import VaultAllocationBars from './VaultAllocationBars'
import Typography from 'components/Common/Typography'
import { getColorGlow, getTokenColor } from 'rfx/lib/utils'
import { keyBy } from 'lodash'
import { TOKENS } from 'config/tokens'
import commonPoolStore from '@store/commonPoolStore'

type VaultAllocationData = {
  address: string
  name: string
  subName: string
  allocationPercentage: string
  marketAddress: string
  allocation?: string
}

export const VaultAllocation = () => {
  const { below } = useBreakpoints()
  const { marketsData } = useMarkets()
  const { tokensData } = useMarketsInfo()
  const { chainId } = useWallet()

  const tokens = keyBy(TOKENS[chainId], 'symbol')
  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAddress = selectedVault.value
  const provider = getProvider(undefined, chainId)
  const contract = new ethers.Contract(
    commonPoolAddress,
    CommonPool.abi,
    provider,
  )
  const { marketTokensData: depositMarketTokensData } = useMarketTokensData({
    isDeposit: true,
  })

  const { data } = useSWR(
    `common-pool-current-allocation-${!!depositMarketTokensData}-${commonPoolAddress}-${chainId}`,
    {
      fetcher: async () => {
        const res = {
          currentAllocation: null,
          allocation: null,
          sum: null,
        }
        if (!depositMarketTokensData) {
          return res
        }
        try {
          const currentAllocation = await contract.currentAllocation()
          let sum = 0
          const allocation = currentAllocation._markets.map(
            (item: string, idx: number) => {
              const _token =
                depositMarketTokensData?.[ethers.utils.getAddress(item)]
              const _allocation = parseFloat(
                (
                  Number(currentAllocation?._balances[idx]) *
                  Math.pow(10, -_token.decimals)
                )
                  .toFixed(4)
                  .toString(),
              )
              //   convertToUsd(
              //     currentAllocation?._balances[idx],
              //     _token.decimals+USD_DECIMALS,
              //     _token.prices.minPrice,
              //   )
              // )
              sum += _allocation
              return _allocation
            },
          )
          return {
            allocation,
            sum,
            currentAllocation,
          }
        } catch (e) {
          return res
        }
      },
    },
  )

  const formattedData: VaultAllocationData[] = useMemo(
    () =>
      data?.currentAllocation?._markets.map((item: string, idx: number) => {
        const address =
          tokensData && marketsData
            ? tokensData[marketsData[item]?.indexTokenAddress]?.symbol
            : '...'

        const poolName = marketsData
          ? marketsData[item]?.name.split(' ')
          : ['...', '...']

        const allocationPercentage = (
          (data?.allocation[idx] / data?.sum) *
          100
        ).toFixed(2)

        return {
          address,
          name: poolName?.[0],
          subName: poolName?.[1],
          marketAddress: data?.currentAllocation._markets[idx],
          allocationPercentage,
          allocation: data?.allocation,
        }
      }),
    [
      data?.allocation,
      data?.currentAllocation?._markets,
      data?.sum,
      marketsData,
      tokensData,
    ],
  )
  const columns: ColumnDef<VaultAllocationData>[] = [
    {
      accessorKey: 'address',
      header: 'Market',
      meta: {
        align: 'left',
      },
      cell: ({ row }) => {
        const data = row.original
        return (
          <div className="flex h-full items-center gap-4">
            <div
              className="h-4 w-4"
              style={{
                background: getTokenColor(data?.name?.split('/')[0]),
                boxShadow: getColorGlow(
                  getTokenColor(data?.name?.split('/')[0]),
                ),
              }}
            />
            <div className="flex flex-col text-sm font-semibold text-th-fgd-1">
              <div>{data.name}</div>
              <div className="font-medium text-th-fgd-3 opacity-90">
                {data.subName}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'Exposure',
      header: () => <div className="w-full text-right uppercase">Exposure</div>,
      meta: {
        align: 'right',
      },
      cell: ({ row }) => {
        const { allocation } = row.original
        return (
          <Typography
            font={'font-number'}
            className="text-base font-semibold text-th-fgd-1 opacity-90"
          >
            ${allocation && allocation[row.index]}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'allocationPercentage',
      header: () => (
        <div className="w-full text-right uppercase">allocation</div>
      ),
      meta: {
        align: 'right',
      },
      cell: ({ row }) => {
        const { allocationPercentage } = row.original
        return (
          <Typography
            font={'font-number'}
            className="text-base font-semibold text-th-fgd-1 opacity-90"
          >
            {`${allocationPercentage}%`}
          </Typography>
        )
      },
    },
  ]

  if (below.lg) {
    return (
      <div className="flex h-full w-full flex-col">
        <div className="flex h-[57px] w-full items-center border-b border-th-input-border px-6 text-sm font-semibold">
          <div className="flex w-full flex-col gap-1">
            <span>Legend</span>
            <VaultAllocationBars tokens={tokens} allocations={formattedData} />
          </div>
        </div>

        <Table
          className="w-full"
          tableClassName="w-full"
          rows={formattedData || []}
          columns={columns}
        />
      </div>
    )
  }

  return (
    <div>
      <div className="flex h-[57px] w-full items-center px-6 text-sm font-semibold">
        <div className="flex w-full flex-col gap-1">
          <span>Legend</span>
          <VaultAllocationBars tokens={tokens} allocations={formattedData} />
        </div>
      </div>

      <div className="h-full">
        <Table rows={formattedData || []} columns={columns} noBorder="true" />
      </div>
    </div>
  )
}
